import MuiTextField from "@mui/material/TextField";
import type { TextFieldProps } from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import { forwardRef } from "react";

const StyledTextField = styled(MuiTextField)(({ theme, error }) => ({
  "& .MuiInputLabel-root": {
    "&.Mui-focused": {
      color: theme.palette.grey[100],
    },
    "&.Mui-disabled": {
      opacity: 0.5,
    },
  },
  "& input, & textarea": {
    caretColor: theme.palette.secondary1[100],
    "::placeholder": {
      opacity: 1,
      color: theme.palette.grey[100],
      fontSize: "14px",
      fontWeight: 400,
    },
  },
  "& .MuiInput-input:focus": {
    backgroundColor: "transparent",
    color: theme.palette.primary.main,
  },

  "& .MuiFormLabel-root": {
    color: error ? theme.palette.error.main : theme.palette.grey[100],
  },

  "& .MuiInput-input": {
    fontSize: "16px",
    lineHeight: "22px",
  },

  "& .MuiSelect-icon": {
    color: theme.palette.primary.main,
  },

  "& label + .MuiInput-root": {
    marginTop: "24px",
  },

  "& .MuiFormHelperText-root": {
    color: error ? theme.palette.error.main : theme.palette.grey[100],
    fontSize: "12px",
    lineHeight: "18px",
  },
  "& .MuiInputBase-sizeSmall, & .MuiInputLabel-sizeSmall": {
    fontSize: "14px",
    lineHeight: "20px",
  },
  "& .MuiOutlinedInput-root": {
    minHeight: 34,
    borderRadius: theme.shape.borderRadiusSmall,
    minWidth: 120,
    "& .MuiInputBase-sizeSmall input": {
      height: "15px",
    },
    "&.MuiInputBase-sizeExtraSmall": {
      maxHeight: "28px",
      minHeight: "30px",
      minWidth: 0,
      "& input.MuiInputBase-input.MuiOutlinedInput-input": {
        paddingTop: 0,
        paddingBottom: 0,
        height: "15px",
      },
    },
    "& fieldset": {
      borderColor: theme.palette.grey[60],
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderWidth: "1px",
    },
  },
  "& .MuiInputBase-root": {
    "&.Mui-disabled": {
      opacity: 0.4,
    },
    "&.MuiInput-underline.Mui-disabled::before": {
      borderBottomStyle: "solid",
    },
    "& .MuiSelect-select": {
      padding: 0,
      paddingTop: "1px",
      paddingBottom: "2px",
    },
  },
  "& .MuiInput-underline": {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(0),
    "&.MuiInputBase-sizeSmall": {
      paddingTop: theme.spacing(0.25),
      paddingBottom: theme.spacing(0),
      paddingLeft: theme.spacing(0),
    },
  },

  ...theme.applyStyles("dark", {
    "&.MuiTextField-root .MuiInput-root": {
      color: theme.palette.common.white,
    },
    "& .MuiInput-input:focus": {
      color: theme.palette.common.white,
    },
    "& .MuiInput-root::before": {
      borderColor: theme.palette.common.white,
      borderWidth: "1px",
    },
    "& .MuiInput-root::after": {
      borderColor: theme.palette.common.white,
      borderWidth: "1px",
    },
    "& .MuiInput-root:hover:not(.Mui-disabled, .Mui-error)::before": {
      borderColor: theme.palette.common.white,
    },
    "& .MuiInput-root:hover:not(.Mui-disabled, .Mui-error)::after": {
      borderColor: theme.palette.common.white,
    },
    "& .MuiInput-htmlInput": {
      color: theme.palette.common.white,
    },
    "& .MuiFormLabel-root": {
      color: error ? theme.palette.error.main : theme.palette.grey[60],
    },
    "& .MuiInputLabel-root": {
      "&.Mui-focused": {
        color: theme.palette.common.white,
      },
    },
  }),
}));

/**
 * @description TextField component has label shrunk by default.
 */
const TextField = forwardRef<HTMLInputElement, TextFieldProps>((props, ref) => {
  const { variant = "standard", slotProps, ...rest } = props;

  return (
    <StyledTextField
      ref={ref}
      variant={variant}
      {...rest}
      slotProps={{
        ...slotProps,
        inputLabel: { shrink: true, ...slotProps?.inputLabel },
      }}
    />
  );
}) as typeof MuiTextField;

export default TextField;
